import { ExchangeCurrency } from "@prisma/client";
import convertPrice from "@/app/utils/CurrencyConverter";
import type { BeginnerAlternative } from "@/app/libs/beginnerAlternatives";
import {
  BookingParticipantData,
  NO_CERTIFICATION_LABEL,
} from "@/app/components/booking/participantTypes";
import { hasRealCertification } from "@/app/libs/participantCertifications";

/**
 * "Book this instead": carries a blocked beginner's booking over to the suggested
 * beginner listing. The form data travels in localStorage (keyed by the target
 * listing) so it also survives the new tab the listing page opens for checkout.
 */

/** Equipment keys of reservationData and the listing price field behind each one. */
export const EQUIPMENT_PRICE_FIELDS = {
  nitrox: "priceNitrox",
  l15: "price15L",
  fullSet: "priceFullSet",
  computer: "priceComputer",
  torch: "priceTorch",
  adapter: "priceAdapter",
  bcd: "priceBCD",
  reg: "priceReg",
  wetsuit: "priceWetsuit",
  mask: "priceMask",
  fins: "priceFins",
  weights: "priceWeights",
  privateGuide: "pricePrivateGuide",
} as const;

export type EquipmentKey = keyof typeof EQUIPMENT_PRICE_FIELDS;
export type EquipmentCounts = Record<EquipmentKey, number>;

type StoredParticipant = Omit<BookingParticipantData, "dateOfBirth" | "lastDive"> & {
  dateOfBirth: string | null;
  lastDive: string | null;
};

export type StoredSnorkeler = {
  name: string;
  lastName: string;
  dateOfBirth: string | null;
  nationality: string;
};

export type BeginnerSwitchDraft = {
  v: 1;
  listingId: string;
  listingTitle: string;
  /** Booking page this draft belongs to; null until the diver picks a date on the listing page. */
  confirmationCode: string | null;
  /** The original date was not bookable: the diver has to pick one on the listing page. */
  needsDate: boolean;
  savedAt: number;
  counts: { participants: number; extraDiver: number; extraSnorkeler: number };
  equipment: EquipmentCounts;
  participants: StoredParticipant[];
  extraDivers: StoredParticipant[];
  extraSnorkelers: StoredSnorkeler[];
  email: string;
  phoneNumber: string;
  request: string;
  acceptedBookingPolicy: boolean;
};

const STORAGE_PREFIX = "buw:beginner-switch:";
const DRAFT_TTL_MS = 2 * 60 * 60 * 1000;

export function saveBeginnerSwitchDraft(draft: BeginnerSwitchDraft): void {
  try {
    window.localStorage.setItem(
      `${STORAGE_PREFIX}${draft.listingId}`,
      JSON.stringify(draft)
    );
  } catch {
    // Storage unavailable (private mode, blocked site data): the switch still works, without prefill.
  }
}

export function readBeginnerSwitchDraft(listingId: string): BeginnerSwitchDraft | null {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${listingId}`);
    if (!raw) return null;
    const draft = JSON.parse(raw) as BeginnerSwitchDraft;
    if (draft?.v !== 1 || draft.listingId !== listingId) return null;
    if (Date.now() - draft.savedAt > DRAFT_TTL_MS) {
      window.localStorage.removeItem(`${STORAGE_PREFIX}${listingId}`);
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

/** Draft to prefill the booking page `confirmationCode` of `listingId`, if any. */
export function readBeginnerSwitchDraftForBooking(
  listingId: string,
  confirmationCode: string
): BeginnerSwitchDraft | null {
  const draft = readBeginnerSwitchDraft(listingId);
  if (!draft) return null;
  if (draft.confirmationCode && draft.confirmationCode !== confirmationCode) return null;
  if (!draft.confirmationCode) {
    // First booking page opened after picking a date: bind the draft to it.
    saveBeginnerSwitchDraft({ ...draft, confirmationCode, needsDate: false });
  }
  return draft;
}

const toIso = (value: unknown): string | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const toDateOrNull = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Participant data for the beginner listing: everything is kept except the
 * certification, which becomes "No certification" when the person has none.
 */
export function toBeginnerParticipant(
  participant: Partial<BookingParticipantData> | undefined
): StoredParticipant {
  const certifications = participant?.certifications ?? [];
  const certified = hasRealCertification(certifications);
  return {
    name: participant?.name ?? "",
    lastName: participant?.lastName ?? "",
    lastDive: toIso(participant?.lastDive),
    loggedDives: Number(participant?.loggedDives) || 0,
    dateOfBirth: toIso(participant?.dateOfBirth),
    nationality: participant?.nationality ?? "",
    certifications: certified ? certifications : [NO_CERTIFICATION_LABEL],
    certificationPhotoUrl: certified ? participant?.certificationPhotoUrl ?? null : null,
    hasDivingInsurance: participant?.hasDivingInsurance ?? null,
    insurancePolicyUrl: participant?.insurancePolicyUrl ?? null,
  };
}

export function toStoredSnorkeler(snorkeler: {
  name?: string;
  lastName?: string;
  dateOfBirth?: Date | string | null;
  nationality?: string;
} | undefined): StoredSnorkeler {
  return {
    name: snorkeler?.name ?? "",
    lastName: snorkeler?.lastName ?? "",
    dateOfBirth: toIso(snorkeler?.dateOfBirth),
    nationality: snorkeler?.nationality ?? "",
  };
}

export function restoreParticipant(stored: StoredParticipant): BookingParticipantData {
  return {
    ...stored,
    // BookingInfo keeps main-diver dates as ISO strings; the DatePicker arrays get Date objects.
    dateOfBirth: stored.dateOfBirth as unknown as Date | null,
    lastDive: stored.lastDive as unknown as Date | null,
  };
}

/** Counts and equipment clamped to what the beginner listing offers (prices are never carried over). */
export function clampToAlternative(
  alternative: BeginnerAlternative,
  counts: { participants: number; extraDiver: number; extraSnorkeler: number },
  equipment: Partial<Record<EquipmentKey, number>>
): { counts: BeginnerSwitchDraft["counts"]; equipment: EquipmentCounts } {
  const offers = (value: number | null | undefined) => Number(value ?? 0) !== 0;
  const clampedEquipment = {} as EquipmentCounts;
  (Object.keys(EQUIPMENT_PRICE_FIELDS) as EquipmentKey[]).forEach((key) => {
    const count = Math.max(0, Number(equipment[key]) || 0);
    clampedEquipment[key] = offers(alternative[EQUIPMENT_PRICE_FIELDS[key]]) ? count : 0;
  });
  return {
    counts: {
      participants: Math.max(1, Number(counts.participants) || 1),
      extraDiver: offers(alternative.priceExtraDiver)
        ? Math.max(0, Number(counts.extraDiver) || 0)
        : 0,
      extraSnorkeler: offers(alternative.priceExtraSnorkeler)
        ? Math.max(0, Number(counts.extraSnorkeler) || 0)
        : 0,
    },
    equipment: clampedEquipment,
  };
}

/**
 * Same totals the listing page reservation widget sends to checkout
 * (ListingClient price conversion + ListingReservation math), for a single-day booking.
 */
export async function buildAlternativeReservationData(params: {
  alternative: BeginnerAlternative;
  counts: BeginnerSwitchDraft["counts"];
  equipment: EquipmentCounts;
  dates: string[];
  currency: string;
  rates: ExchangeCurrency | null;
  confirmationCode: string;
}) {
  const { alternative, counts, equipment, dates, currency, rates, confirmationCode } = params;
  const listingCurrency = alternative.currency || "";
  const skipConversion = currency === "USD" && alternative.currency === "USD";
  const convert = async (value: number | null | undefined) => {
    const amount = Number(value ?? 0);
    if (skipConversion) return amount;
    const converted = await convertPrice(amount, listingCurrency, currency, rates);
    return parseFloat(converted.toFixed(0));
  };

  const price = await convert(alternative.price);
  const priceExtraDiver = await convert(alternative.priceExtraDiver);
  const priceExtraSnorkeler = await convert(alternative.priceExtraSnorkeler);
  const priceFees = await convert(alternative.priceFees);

  let equipmentTotal = 0;
  for (const key of Object.keys(EQUIPMENT_PRICE_FIELDS) as EquipmentKey[]) {
    if (!equipment[key]) continue;
    equipmentTotal += (await convert(alternative[EQUIPMENT_PRICE_FIELDS[key]])) * equipment[key];
  }

  const totalPrice =
    price * counts.participants +
    priceExtraDiver * counts.extraDiver +
    priceExtraSnorkeler * counts.extraSnorkeler +
    equipmentTotal;
  const totalFees = priceFees && counts.participants ? priceFees * counts.participants : 0;
  const discountTotal = alternative.discountTotal ?? 0;
  const totalPriceAfterDiscount =
    discountTotal !== 0 ? totalPrice - (totalPrice * discountTotal) / 100 : 0;
  const feesNotIncluded = !!alternative.notIncluded?.includes("Fees");
  const totalPriceAfterFees = feesNotIncluded
    ? (totalPriceAfterDiscount !== 0 ? totalPriceAfterDiscount : totalPrice) + totalFees
    : 0;

  return {
    totalPrice,
    currency,
    dates,
    participants: counts.participants,
    extraDiver: counts.extraDiver,
    extraSnorkeler: counts.extraSnorkeler,
    ...equipment,
    totalFees,
    selectedDate: dates[0] ?? null,
    status: alternative.automaticApproval === true ? "accepted" : "pending",
    confirmationCode,
    totalPriceAfterDiscount,
    totalPriceAfterFees,
  };
}
